import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { useNavigate } from 'react-router-dom';

/**
 * Route a push tap to the right in-app screen. Mirrors the payload allowlist
 * categories defined in backend/src/services/pushPayloads.js. Unknown types
 * default to dashboard so we never crash on a future category.
 */
function routeFor(data: Record<string, string> | undefined): string {
  if (!data?.type) return '/dashboard';
  switch (data.type) {
    // ?handoff= had no reader in ChatPage — plain /chat is the honest landing.
    // The chat surface itself shows the handoff state; do not invent a param.
    case 'handoff_nudge':   return '/chat';
    case 'trfer_claimed':
    case 'trfer_message':
    case 'handback':        return data.conversation_id ? `/chat/${data.conversation_id}` : '/chat';
    // #2354: TasksPage opens the Done group when the deep-linked id is a
    // finished task, so a completed task lands on its own row too.
    case 'task_due':
    case 'task_completed':  return data.task_id ? `/tasks/${data.task_id}` : '/tasks';
    // #2354: bell-only (no push category). The feed shows the poll card; the
    // stored `#poll-<id>` deep_link names an anchor no page renders, so the
    // hash is not carried.
    // w1023c L1: check-ins and form sends are answered on Today, so their
    // publish rows store '/dashboard'; a quick poll still lands on Community.
    case 'poll_published':  return data.link?.startsWith('/dashboard') ? '/dashboard' : '/community';
    // The family's Grants page (#2341). `/bills` has been the parent Grants
    // page since the 2026-09-21 rebuild (nav label "Grants"; `/grants`
    // redirects to it). It has no per-grant route and no tab row, so the
    // grant_id is not carried and no `?tab=` is added — a phantom
    // `/grants/:id` would fall through to the catch-all (the B-065 trap).
    case 'grant_decision':  return '/bills';
    // #2655: a reimbursement TRF sent back as needs_info. Its receipt is on the
    // same Grants page; no per-claim route exists, so claim_id is not carried.
    case 'claim_update':    return '/bills';
    // Staff-only category (F-023): the recipient is always the family's
    // assigned TRFer, and this hook only ever runs on native (see the
    // isNativePlatform guard below). The native TRFer shell is `/trfer/m/*`
    // (design-grammar §4.1) — `/trfer/calendar` sits inside MobileRouteGuard,
    // which on native renders the "built for the website" wall instead of the
    // page, so the old target turned every tap into a dead end. The payload
    // carries family_id, so land on that family's lite profile; fall back to
    // the native Today surface, which is homeFor('trfer') on native.
    case 'calendar_reminder': return data.family_id ? `/trfer/m/family/${data.family_id}` : '/trfer/m';
    // Staff-only (wave w1024): a family became this TRFer's. Same native
    // shell rule as calendar_reminder — `/trfer/families*` sits inside
    // MobileRouteGuard, so on native it is the "built for the website" wall.
    // One family carries family_id and opens its lite profile; a bulk assign
    // carries no id and lands on Today, the native home. The email carries
    // the desktop links (/trfer/families/:id, /trfer/families?scope=mine).
    case 'family_assigned':   return data.family_id ? `/trfer/m/family/${data.family_id}` : '/trfer/m';
    // #2354: a producer that knows its page says so in `link` (the bell
    // bridges it from `metadata.deep_link`), e.g. the "reimbursement
    // approved" `general` row → `/bills`. Only an in-app path is honoured.
    default:                return isInAppPath(data.link) ? data.link : '/dashboard';
  }
}

function isInAppPath(link: string | undefined): link is string {
  return !!link && link.startsWith('/') && !link.startsWith('//') && !link.includes('\\');
}

export function usePushDeepLink() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handler: { remove: () => void } | null = null;

    (async () => {
      // 1) Replay any push action stashed by MainActivity.stashFcmAction before
      //    the WebView reload (B-063 fix). The Java side writes FCM extras to
      //    CapacitorStorage under key 'pending_push_action' as JSON when the
      //    activity is launched/resumed via an FCM tap. We consume it here on
      //    mount so the tap routes correctly even when the JS context reset
      //    happens before the addListener call below.
      try {
        const pending = await Preferences.get({ key: 'pending_push_action' });
        if (pending.value) {
          await Preferences.remove({ key: 'pending_push_action' });
          const data = JSON.parse(pending.value) as Record<string, string>;
          navigate(routeFor(data));
        }
      } catch (e) {
        console.warn('[push] pending_push_action replay failed', e);
      }

      // 2) Normal listener for warm-path taps where the JS context survives.
      //    Crucially we do NOT clear pending_push_action here — when the
      //    WebView reloads on tap (B-063 observed behavior), stale listeners
      //    from the pre-reload context fire and would clear the stash before
      //    the freshly-mounted React app's init poll above could read it.
      //    Only the init poll clears the stash. Risk: session-N's last warm
      //    tap leaves a stale stash that triggers on session-(N+1) cold
      //    launch. Acceptable in practice — most cold launches coincide with
      //    a fresh intent-driven tap, which overwrites the stash.
      handler = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        navigate(routeFor(action.notification?.data as Record<string, string> | undefined));
      });
    })();

    return () => { handler?.remove(); };
  }, [navigate]);
}

export { routeFor };  // exported for unit tests
